import type { Flow, Offer, Dimension, Widget } from '@dashboard/types/config'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { useOfferStore } from '@dashboard/modules/offer/stores/offerStore'
import { useSegmentGroupStore } from '@dashboard/modules/segment/stores/segmentGroupStore'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'

export const uuid = () => {
  let d = Date.now()

  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now() //use high-precision timer if available
  }

  return ('xxxxxxxx' + 'xxxx' + '4xxx' + 'yxxx' + 'xxxxxxxxxxxx').replace(/[xy]/g, function (c) {
    const r = Math.trunc((d + Math.random() * 16) % 16)
    d = Math.floor(d / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export const requestUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const raw = uuid()
  return `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20, 32)}`
}

export const idPrefixes = {
  ACTION: 'act',
  CAMPAIGN: 'cpn',
  DIMENSION: 'dim',
  GOAL: 'gol',
  GOAL_EVENT: 'gev',
  SEGMENT: 'seg',
  SEGMENT_GROUP: 'dim',
  CAMPAIGN_ACTION: 'act',
  RIGHT_BAR: 'rbr',
  RIGHT_BAR_VARIANT: 'rbv',
  RIGHT_ASK_QUESTION: 'raq',
  RIGHT_ASK_QUESTION_OPTION: 'rqo',
  WIDGET: 'wdg',
  FLOW: 'ofn',
  OFFER: 'ofr',
  PHRASE: 'phr',
  VARIANT: 'var'
}

export const makeId = (prefix: string, invalidIds: string[] = [], length: number = 8): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let id

  if (invalidIds.length === 0) {
    if (prefix === idPrefixes.FLOW) {
      const flowStore = useFlowStore()
      invalidIds = flowStore.flows.map((f: Flow) => f.id)
    } else if (prefix === idPrefixes.OFFER) {
      const offerStore = useOfferStore()
      invalidIds = offerStore.offers.map((o: Offer) => o.id)
    } else if (prefix === idPrefixes.DIMENSION) {
      const segmentGroupStore = useSegmentGroupStore()
      invalidIds = segmentGroupStore.segmentGroups.map((sg: Dimension) => sg.id)
    } else if (prefix === idPrefixes.WIDGET) {
      const widgetStore = useWidgetStore()
      invalidIds = widgetStore.widgets.map((w: Widget) => w.id)
    }
  }

  do {
    id = prefix + '_'

    for (let i = length; i > 0; --i) {
      id += chars[Math.floor(Math.random() * chars.length)]
    }
  } while (invalidIds.includes(id))

  return id
}

export const inferType = (object: { id?: string } | null | undefined): keyof typeof idPrefixes | null => {
  if (!object?.id) {
    return null
  }

  const idPrefix = object.id.slice(0, 3)
  const prefixType = (Object.keys(idPrefixes) as (keyof typeof idPrefixes)[]).find(
    (key) => idPrefixes[key] === idPrefix
  )

  return prefixType ?? null
}
